import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import NavBar from './layout/NavBar';
import Config from '../config';

const Validate = () => {
  const [formData, setFormData] = useState({
    sicknoteId: '',
    firstName: '',
    lastName: '',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [recaptcha, setRecaptcha] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');

  const messageRef = useRef(null);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    // Check if ReCAPTCHA token is empty
    if (!recaptcha) {
      setRecaptchaError('Please complete the ReCAPTCHA verification.');
      setLoading(false);
      return;
    }

    // Basic validation
    if (!formData.sicknoteId || !formData.firstName || !formData.lastName || !formData.startDate || !formData.endDate) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${Config.service}/services/sicknote/validateById/${formData.sicknoteId}`, {
        params: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          startDate: formData.startDate,
          endDate: formData.endDate,
          recaptcha
        }
      });

      // Reset ReCAPTCHA error
      setRecaptchaError('');

      if (response.data.success) {
        if (response.data.sickNote) {
          const { user, startTimeOff, endOfTimeOff } = response.data.sickNote;

          setResult({
            success: true,
            message: 'Sick note is valid and verified.',
            sickNote: {
              ...response.data.sickNote,
              user: {
                firstName: user.firstName,
                lastName: user.lastName
              },
              startDate: moment(startTimeOff).format('YYYY-MM-DD'),
              endDate: moment(endOfTimeOff).format('YYYY-MM-DD')
            }
          });
        } else {
          setResult({
            success: false,
            message: response.data.message || 'This sick note is invalid.'
          });
        }
      } else {
        setResult({
          success: false,
          message: response.data.message // Backend error message
        });
      }

      // Scroll to message box and apply bounce animation
      if (messageRef.current) {
        messageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        messageRef.current.classList.add('animated-zoom-in');
      }
    } catch (error) {
      console.error('Error validating sick note:', error.message);
      setResult({
        success: false,
        message: 'Something went wrong!'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sicknoteId: '',
      firstName: '',
      lastName: '',
      startDate: '',
      endDate: ''
    });
    setResult(null);
    setError('');
    setRecaptcha('');
    setRecaptchaError('');
  };

  return (
    <>
      <Helmet>
        <title>Validate Sick Note | Richmond In-Store Medical Clinic</title>
        <meta name="description" content="Verify the authenticity of a sick note issued by Richmond In-Store Medical Clinic. Enter sick note details to validate legitimacy." />
        <link rel="canonical" href="https://instoremed.com/validate" />
      </Helmet>

      <NavBar />

      <main className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="center mb-4">
                <h1 className="mb-3">
                  Validate Sick <span className="keywords">Note</span>
                </h1>
                <div className="bottom-line"></div>
                <p className="lead">Verify the authenticity of a sick note issued by Richmond In-Store Medical Clinic</p>
              </div>

              <article className="card">
                <div>
                  <h3 className="mb-1 text-center">Sick Note Verification</h3>
                  <div className="bottom-line"></div>
                  <p className="mb-3 text-center">Enter the details below to verify if a sick note is legitimate and was issued by our clinic.</p>

                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="sicknoteId" className="form-label">
                          <strong>Sick Note ID *</strong>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="sicknoteId"
                          name="sicknoteId"
                          value={formData.sicknoteId}
                          onChange={handleInputChange}
                          placeholder="Enter sick note ID"
                          required
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="firstName" className="form-label">
                          <strong>First Name *</strong>
                        </label>
                        <input type="text" className="form-control" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Enter first name" required />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="lastName" className="form-label">
                          <strong>Last Name *</strong>
                        </label>
                        <input type="text" className="form-control" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Enter last name" required />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="startDate" className="form-label">
                          <strong>Start Date *</strong>
                        </label>
                        <input type="date" className="form-control" id="startDate" name="startDate" value={formData.startDate} onChange={handleInputChange} required />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="endDate" className="form-label">
                          <strong>End Date *</strong>
                        </label>
                        <input type="date" className="form-control" id="endDate" name="endDate" value={formData.endDate} onChange={handleInputChange} required />
                      </div>
                    </div>

                    {/* ReCAPTCHA component */}
                    <div className="center mb-3">
                      <ReCAPTCHA className="my-2" sitekey={Config.captcha_key} onChange={val => setRecaptcha(val)} />
                      {recaptchaError && <div className="text-danger">{recaptchaError}</div>}
                    </div>

                    <div className="center mb-3">
                      <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                        {loading ? 'Validating...' : 'Validate Sick Note'}
                      </button>

                      {(result || error) && (
                        <button type="button" className="btn btn-secondary btn-lg ms-2" onClick={resetForm}>
                          Reset Form
                        </button>
                      )}
                    </div>
                  </form>

                  <style>
                    {`
                      @keyframes zoomIn {
                        0% {
                          transform: scale(0);
                          opacity: 0;
                        }
                        100% {
                          transform: scale(1);
                          opacity: 1;
                        }
                      }

                      .animated-zoom-in {
                        animation-name: zoomIn;
                        animation-duration: 0.5s;
                      }
                    `}
                  </style>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <strong>Error:</strong> {error}
                    </div>
                  )}

                  {result && (
                    <div className="mt-4" ref={messageRef}>
                      {result.success ? (
                        <div className="alert alert-success fs-6" role="alert">
                          <strong>Validation Successful!</strong> This sick note is valid. Patient's information:{' '}
                          {`Name: ${result.sickNote.user.firstName} ${result.sickNote.user.lastName}, Start Date: ${result.sickNote.startDate}, End Date: ${result.sickNote.endDate}`}
                        </div>
                      ) : (
                        <div className="alert alert-danger fs-6" role="alert">
                          <strong>Validation Failed!</strong> {result.message}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4">
                    <h4>How to Use This Validation</h4>
                    <ul>
                      <li>
                        Enter the <strong>Sick Note ID</strong> found on the sick note document
                      </li>
                      <li>
                        Enter the patient's <strong>first and last name</strong> exactly as shown on the sick note
                      </li>
                      <li>
                        Enter the <strong>start and end dates</strong> of the sick leave period
                      </li>
                      <li>Click "Validate Sick Note" to verify authenticity</li>
                    </ul>

                    <p className="mt-3">
                      <strong>Note:</strong> This validation system helps employers and educational institutions verify that sick notes were legitimately issued by Richmond In-Store Medical Clinic.
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Validate;
